// src/components/HouseholdModal.ts
// Household selector, settings, member roster, and role administration

import type { Household, Member, UserRole } from '../types';
import { api } from '../services/api';
import { showToast } from './Toast';

export class HouseholdModal {
  private el: HTMLElement;
  private currentHousehold: Household | null = null;
  private households: Household[] = [];
  private members: Member[] = [];
  private isOwner = false;

  constructor(
    private callbacks: {
      onSelectHousehold: (household: Household) => void;
      onOpenInvite: (household: Household) => void;
      onHouseholdUpdated: () => void;
    }
  ) {
    this.el = document.createElement('div');
    this.el.className = 'modal-overlay';
    document.body.appendChild(this.el);

    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) this.close();
    });
  }

  public async open(current: Household | null, all: Household[]) {
    this.currentHousehold = current;
    this.households = all;
    this.isOwner = current?.role === 'owner';

    this.render();
    this.el.classList.add('open');

    if (this.currentHousehold) {
      await this.loadMembers();
    }
  }

  public close() {
    this.el.classList.remove('open');
  }

  private async loadMembers() {
    if (!this.currentHousehold) return;
    try {
      const res = await api.getMembers(this.currentHousehold.id);
      this.members = res.members;
      this.render();
    } catch (err: any) {
      showToast('Error loading members: ' + err.message);
    }
  }

  private render() {
    if (!this.currentHousehold) {
      this.el.innerHTML = `
        <div class="modal-card">
          <div class="modal-header">
            <h2 class="modal-title">Household</h2>
            <button class="btn-icon" id="hh-close-btn">✕</button>
          </div>
          <div class="modal-body">
            <p>No active household selected.</p>
          </div>
        </div>
      `;
      this.el.querySelector('#hh-close-btn')?.addEventListener('click', () => this.close());
      return;
    }

    const { name, role } = this.currentHousehold;

    // Household list dropdown options
    const householdOptions = this.households
      .map(
        (h) =>
          `<option value="${h.id}" ${h.id === this.currentHousehold?.id ? 'selected' : ''}>
            ${escapeHtml(h.name)} (${h.role})
          </option>`
      )
      .join('');

    // Members list items
    const membersHtml = this.members.length
      ? this.members
          .map((m) => {
            const roleSelect =
              this.isOwner && !m.isCurrentUser
                ? `<select class="member-role-select input-field" data-user-id="${m.userId}" style="padding:0.25rem 0.5rem;font-size:0.78rem;">
                    <option value="owner" ${m.role === 'owner' ? 'selected' : ''}>Owner</option>
                    <option value="editor" ${m.role === 'editor' ? 'selected' : ''}>Editor</option>
                    <option value="shopper" ${m.role === 'shopper' ? 'selected' : ''}>Shopper</option>
                   </select>`
                : `<span class="role-badge ${m.role}">${m.role}</span>`;

            const removeBtn =
              (this.isOwner && !m.isCurrentUser) || m.isCurrentUser
                ? `<button class="btn-icon member-remove-btn" data-user-id="${m.userId}" title="${m.isCurrentUser ? 'Leave Household' : 'Remove Member'}" style="width:28px;height:28px;font-size:0.75rem;color:var(--rose);">
                    ✕
                   </button>`
                : '';

            return `
              <div style="display:flex;align-items:center;justify-content:space-between;gap:0.75rem;padding:0.6rem 0.8rem;background:var(--bg-subtle);border-radius:var(--radius-sm);">
                <div style="display:flex;align-items:center;gap:0.6rem;min-width:0;">
                  <img style="width:32px;height:32px;border-radius:50%;object-fit:cover;"
                       src="${m.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name)}&background=5C7A5F&color=fff`}"
                       alt="${escapeHtml(m.name)}" />
                  <div style="min-width:0;">
                    <div style="font-weight:600;font-size:0.88rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                      ${escapeHtml(m.name)} ${m.isCurrentUser ? '<span style="font-size:0.7rem;color:var(--sage);">(You)</span>' : ''}
                    </div>
                    <div style="font-size:0.75rem;color:var(--ink-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                      ${escapeHtml(m.email)}
                    </div>
                  </div>
                </div>
                <div style="display:flex;align-items:center;gap:0.4rem;">
                  ${roleSelect}
                  ${removeBtn}
                </div>
              </div>
            `;
          })
          .join('')
      : `<div style="text-align:center;color:var(--ink-muted);padding:1rem;">Loading members…</div>`;

    this.el.innerHTML = `
      <div class="modal-card">
        <div class="modal-header">
          <h2 class="modal-title">Household Settings</h2>
          <button class="btn-icon" id="hh-close-btn">✕</button>
        </div>

        <div class="modal-body">
          <!-- Switch Household -->
          <div style="display:flex;gap:0.5rem;align-items:center;">
            <select class="input-field" id="switch-hh-select" style="flex:1;">
              ${householdOptions}
            </select>
            <button class="btn btn-secondary" id="show-new-hh-btn" title="Create new household">+ New</button>
          </div>

          <!-- Create New Household Inline Form -->
          <div id="new-hh-form" style="display:none;background:var(--bg-subtle);padding:0.75rem;border-radius:var(--radius-sm);">
            <div style="font-size:0.85rem;font-weight:600;margin-bottom:0.4rem;">Create New Household</div>
            <div style="display:flex;gap:0.5rem;">
              <input type="text" class="input-field" id="new-hh-name" placeholder="e.g. Smithson Home, Holiday House" style="flex:1;" />
              <button class="btn btn-primary" id="create-hh-submit-btn">Create</button>
              <button class="btn btn-ghost" id="cancel-new-hh-btn">Cancel</button>
            </div>
          </div>

          <!-- Members Section -->
          <div style="margin-top:0.5rem;">
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:0.6rem;">
              <div style="font-size:0.95rem;font-weight:600;">Members (${this.members.length})</div>
              ${
                this.isOwner
                  ? `<button class="btn btn-sage" id="open-invite-btn" style="padding:0.35rem 0.75rem;font-size:0.8rem;">
                       ➕ Invite Member
                     </button>`
                  : ''
              }
            </div>

            <div style="display:flex;flex-direction:column;gap:0.45rem;">
              ${membersHtml}
            </div>
          </div>
        </div>
      </div>
    `;

    // Events
    this.el.querySelector('#hh-close-btn')?.addEventListener('click', () => this.close());

    this.el.querySelector('#switch-hh-select')?.addEventListener('change', (e: any) => {
      const selected = this.households.find((h) => h.id === e.target.value);
      if (selected && selected.id !== this.currentHousehold?.id) {
        this.callbacks.onSelectHousehold(selected);
        this.close();
      }
    });

    const newHhForm = this.el.querySelector('#new-hh-form') as HTMLElement;
    this.el.querySelector('#show-new-hh-btn')?.addEventListener('click', () => {
      if (newHhForm) {
        newHhForm.style.display = newHhForm.style.display === 'none' ? 'block' : 'none';
        (this.el.querySelector('#new-hh-name') as HTMLElement)?.focus();
      }
    });
    this.el.querySelector('#cancel-new-hh-btn')?.addEventListener('click', () => {
      if (newHhForm) newHhForm.style.display = 'none';
    });

    this.el.querySelector('#create-hh-submit-btn')?.addEventListener('click', async () => {
      const nameInput = this.el.querySelector('#new-hh-name') as HTMLInputElement;
      const val = nameInput?.value.trim();
      if (!val) return;
      try {
        const res = await api.createHousehold(val);
        showToast(`Created household "${val}"`);
        this.callbacks.onHouseholdUpdated();
        this.callbacks.onSelectHousehold(res.household);
        this.close();
      } catch (err: any) {
        showToast('Failed to create household: ' + err.message);
      }
    });

    this.el.querySelector('#open-invite-btn')?.addEventListener('click', () => {
      if (this.currentHousehold) {
        this.callbacks.onOpenInvite(this.currentHousehold);
        this.close();
      }
    });

    // Member role updates
    this.el.querySelectorAll('.member-role-select').forEach((sel) => {
      sel.addEventListener('change', async (e: any) => {
        const targetUserId = sel.getAttribute('data-user-id')!;
        const newRole = e.target.value as UserRole;
        try {
          await api.updateMemberRole(this.currentHousehold!.id, targetUserId, newRole);
          showToast('Updated member role');
          await this.loadMembers();
        } catch (err: any) {
          showToast('Error: ' + err.message);
          await this.loadMembers();
        }
      });
    });

    // Member remove/leave
    this.el.querySelectorAll('.member-remove-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const targetUserId = btn.getAttribute('data-user-id')!;
        const isSelf = targetUserId === this.members.find((m) => m.isCurrentUser)?.userId;
        const confirmMsg = isSelf
          ? 'Are you sure you want to leave this household?'
          : 'Remove this member from the household?';

        if (confirm(confirmMsg)) {
          try {
            await api.removeMember(this.currentHousehold!.id, targetUserId);
            showToast(isSelf ? 'You left the household' : 'Member removed');
            if (isSelf) {
              this.callbacks.onHouseholdUpdated();
              this.close();
            } else {
              await this.loadMembers();
            }
          } catch (err: any) {
            showToast('Error: ' + err.message);
          }
        }
      });
    });
  }
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
